import { createFileRoute } from '@tanstack/react-router'
import { useId } from 'react'

import { ControlRegister } from '@/components/corporate/control-register'
import { DirectorsRegister, OfficersRegister } from '@/components/corporate/people-registers'
import { SharesRegister } from '@/components/corporate/shares-register'
import { Frame } from '@/components/frame'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { formatDateOnly } from '@/lib/format'
import { matching, oneOf, readSearch } from '@/lib/search-params'

const TABS = ['administrateurs', 'dirigeants', 'actions', 'controle'] as const

export const Route = createFileRoute('/_app/corporate/registres')({
  validateSearch: (search: Record<string, unknown>) =>
    readSearch({ registre: oneOf(TABS), au: matching(/^\d{4}-\d{2}-\d{2}$/) }, search),
  component: RegistersPage,
})

/**
 * Les registres du livre (lot 45) : administrateurs, dirigeants, actions et
 * particuliers ayant un contrôle important. Chacun se lit à une date (« État
 * au … ») : le livre se reconstitue à n'importe quel jour passé.
 */
function RegistersPage() {
  const { registre, au } = Route.useSearch()
  const navigate = Route.useNavigate()
  const dateId = useId()
  const tab = registre ?? 'administrateurs'
  return (
    <div className="space-y-4" data-testid="registres">
      <div className="flex flex-wrap items-end gap-3">
        <div className="space-y-1">
          <Label htmlFor={dateId}>État au</Label>
          <Input
            id={dateId}
            type="date"
            className="w-44"
            value={au ?? ''}
            onChange={(event) => {
              void navigate({
                search: (prev) => ({ ...prev, au: event.target.value || undefined }),
                replace: true,
              })
            }}
          />
        </div>
        <p className="pb-2 text-sm text-muted-foreground" data-testid="registres-date">
          {au
            ? `Les registres tels qu’ils étaient le ${formatDateOnly(au)}.`
            : 'Les registres aujourd’hui.'}
        </p>
      </div>
      <Tabs
        value={tab}
        onValueChange={(next) => {
          void navigate({
            search: (prev) => ({ ...prev, registre: next as (typeof TABS)[number] }),
            replace: true,
          })
        }}
      >
        <TabsList className="h-auto flex-wrap">
          <TabsTrigger value="administrateurs">Administrateurs</TabsTrigger>
          <TabsTrigger value="dirigeants">Dirigeants</TabsTrigger>
          <TabsTrigger value="actions">Actions</TabsTrigger>
          <TabsTrigger value="controle">Contrôle important (art. 18.1)</TabsTrigger>
        </TabsList>
        <TabsContent value="administrateurs">
          <Frame>
            <DirectorsRegister asOf={au} />
          </Frame>
        </TabsContent>
        <TabsContent value="dirigeants">
          <Frame>
            <OfficersRegister asOf={au} />
          </Frame>
        </TabsContent>
        <TabsContent value="actions">
          <Frame>
            <SharesRegister asOf={au} />
          </Frame>
        </TabsContent>
        <TabsContent value="controle">
          <Frame>
            <ControlRegister asOf={au} />
          </Frame>
        </TabsContent>
      </Tabs>
    </div>
  )
}
